/**
 * 共享 SSE 基础设施 —— jerry 所有 `text/event-stream` 消费端的公共实现。
 *
 * 原生 EventSource 只支持 GET 且不能携带 Authorization 头，因此各流式端点统一用
 * fetch + ReadableStream 手工解析 SSE 帧（`event:` + `data:`，空行分帧，`:` 注释行
 * 与 `id:` 行按需处理）。此前 draftDoc / dueDiligence / publicResearch 各自复制了
 * 一份几乎相同的帧解析器，这里抽取为单一实现，供起草、尽调、公开调研、会话聊天
 * 四条流复用。
 *
 * 三个导出：
 *  - {@link createSseFrameParser} —— 增量帧解析器，喂入任意切分的文本块，凑齐一帧
 *    即回调 `(eventType, data, id)`；`id` 供断线重连锚点（GET 长流）使用，POST 流
 *    忽略即可。
 *  - {@link readSsePostStream} —— 消费一个已 200 的 SSE 响应体：驱动解析器分发事件，
 *    并在解析到终态事件（默认 `done`）后主动取消读取，避免「终态已分发、连接未关闭」
 *    窗口内的取消竞态把已持久化的轮次误判为失败。
 *  - {@link startSseLoop} —— GET 长流的建连/重连/降级调度循环（通知中心与
 *    useEventStream 共用；切片 2 从 useNotifications 下沉，行为口径不变）。
 */

import {
  ApiError,
  authHeaders,
  fetchWithAuthRetry,
  throwApiErrorFromResponse,
} from "./index";

/** SSE 帧回调；`id` 为 `id:` 行的值（无则为 null），供断线重连锚点使用。 */
export type SseFrameHandler = (
  eventType: string,
  data: unknown,
  id: string | null,
) => void;

/**
 * 增量解析 text/event-stream：喂入任意切分的文本块，凑齐一帧（空行分隔）即触发
 * 回调。`:keepalive` 等注释行被透明丢弃；`data:` 非法 JSON 时该帧被丢弃，不中断
 * 整条流。独立导出以便单元测试跨块边界 / CRLF / 残余帧的解析行为。
 */
export function createSseFrameParser(onEvent: SseFrameHandler) {
  let buffer = "";

  function emitFrame(frame: string) {
    let eventType = "";
    let id: string | null = null;
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) {
        eventType = line.slice("event:".length).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trimStart());
      } else if (line.startsWith("id:")) {
        id = line.slice("id:".length).trim();
      }
      // 其余行（注释 `:`、含 `: keepalive`）忽略。
    }
    if (!eventType || dataLines.length === 0) return;
    let data: unknown;
    try {
      data = JSON.parse(dataLines.join("\n"));
    } catch {
      return; // 载荷非法：丢弃该帧，不中断整条流。
    }
    onEvent(eventType, data, id);
  }

  return {
    feed(chunk: string) {
      buffer += chunk;
      // 帧以空行结束；兼容 \r\n 换行。
      let idx: number;
      while ((idx = buffer.search(/\r?\n\r?\n/)) >= 0) {
        const frame = buffer.slice(0, idx).replace(/\r\n/g, "\n");
        buffer = buffer.slice(idx).replace(/^\r?\n\r?\n/, "");
        emitFrame(frame);
      }
    },
    /** 流结束时冲刷残余（正常流以空行收尾，通常无残余）。 */
    flush() {
      const rest = buffer.trim();
      buffer = "";
      if (rest) emitFrame(rest.replace(/\r\n/g, "\n"));
    },
  };
}

/**
 * 消费一个已成功（200）的 SSE 响应体：驱动帧解析器逐帧分发，并在解析到终态事件后
 * 主动结束读取。
 *
 * @param response  已 `res.ok` 的 fetch 响应（流开始前的错误应由调用方在此之前用
 *                  `throwApiErrorFromResponse` 处理）。
 * @param onFrame   帧回调 `(eventType, data, id)`。
 * @param options.doneEventType  终态事件名，解析到即主动 `reader.cancel()` 并返回，
 *   不再等服务端关闭连接（默认 `"done"`）。传 `null` 关闭该行为（用于服务端主动关闭
 *   连接的长流，如项目级/公开调研 GET 流）。
 *
 * resolve 于终态事件或流被服务端读尽；流中途网络断开抛原生错误（调用方按「本轮中断」
 * 处理，正文已由后端保留）；`reader` 释放在 finally 中完成。
 */
export async function readSsePostStream(
  response: Response,
  onFrame: SseFrameHandler,
  options?: { doneEventType?: string | null },
): Promise<void> {
  if (!response.body) {
    throw new ApiError(0, "流式响应不可读（浏览器不支持或响应无 body）");
  }
  const doneEventType =
    options && "doneEventType" in options ? options.doneEventType : "done";

  let sawDone = false;
  const parser = createSseFrameParser((eventType, data, id) => {
    if (doneEventType != null && eventType === doneEventType) sawDone = true;
    onFrame(eventType, data, id);
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
      if (sawDone) {
        await reader.cancel().catch(() => undefined);
        return;
      }
    }
    parser.feed(decoder.decode());
    parser.flush();
  } finally {
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// GET 长流连接循环 —— 通知中心 SSE 与 useEventStream 的公共调度实现
// ---------------------------------------------------------------------------

/** 建连失败到降级的默认阈值与节奏（与通知中心切片 1 落地口径一致）。 */
const SSE_LOOP_DEFAULT_MAX_FAILURES = 5;
const SSE_LOOP_DEFAULT_RECONNECT_MS = 3_000;
const SSE_LOOP_DEFAULT_DEGRADED_RETRY_MS = 60_000;

export interface SseLoopOptions {
  /** 每次尝试的请求地址；重连前重新求值（携带消费方更新后的游标）。 */
  url: () => string;
  /**
   * 建连判定事件名：只有收到该帧才算建连成功。HTTP 200 但流立即关闭
   * （网关截断/代理故障）不算——计入连续失败，达到上限后触发降级。
   */
  establishmentEvent: string;
  /** 每个解析帧的回调（含建连帧本身），由消费方赋予领域语义。 */
  onFrame: SseFrameHandler;
  /** 任一次连接尝试结束（无论成败、无论是否建连成功）且未被停止时回调。 */
  onDisconnect?: () => void;
  /** 连续失败达到上限后的回调（消费方进入降级态；此后重连间隔切为分钟级探测）。 */
  onDegraded: () => void;
  /** 连续建连失败上限，默认 5。 */
  maxConsecutiveFailures?: number;
  /** 常规重连间隔，默认 3s。 */
  reconnectDelayMs?: number;
  /** 降级后的 SSE 探测间隔，默认 60s。 */
  degradedRetryDelayMs?: number;
}

export interface SseLoopHandle {
  /** 停止循环：中止当前连接、取消已排期的重连；不可重入。 */
  stop: () => void;
}

/**
 * 建立并维护一条 GET 长流 SSE 连接：建连（以 establishmentEvent 帧为准）→
 * 断开/失败后按间隔重连 → 连续失败超上限后以分钟级间隔继续探测。
 *
 * 调用方语义（通知中心 / useEventStream 共同遵守）：
 *  - 存活过的连接（收到过建连帧）掉线不累计失败；
 *  - 网络断开 / 非 2xx / 响应无 body / 空 200 流统一走重连调度；
 *  - `stop()` 后不再有任何回调与重试。
 */
export function startSseLoop(opts: SseLoopOptions): SseLoopHandle {
  const maxFailures = opts.maxConsecutiveFailures ?? SSE_LOOP_DEFAULT_MAX_FAILURES;
  const reconnectDelayMs = opts.reconnectDelayMs ?? SSE_LOOP_DEFAULT_RECONNECT_MS;
  const degradedRetryDelayMs =
    opts.degradedRetryDelayMs ?? SSE_LOOP_DEFAULT_DEGRADED_RETRY_MS;

  let cancelled = false;
  let failures = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let activeController: AbortController | undefined;

  const scheduleReconnect = (delayMs: number) => {
    if (cancelled) return;
    reconnectTimer = setTimeout(connect, delayMs);
  };

  const readStream = async (response: Response): Promise<boolean> => {
    if (!response.body) throw new TypeError("SSE 响应无 body");
    let sawEstablished = false;
    const parser = createSseFrameParser((eventType, data, id) => {
      if (eventType === opts.establishmentEvent) sawEstablished = true;
      opts.onFrame(eventType, data, id);
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    parser.flush();
    return sawEstablished;
  };

  async function connect(): Promise<void> {
    const controller = new AbortController();
    activeController = controller;
    let established = false;
    try {
      const url = opts.url();
      const res = await fetchWithAuthRetry(url, {
        headers: { Accept: "text/event-stream", ...authHeaders() },
        signal: controller.signal,
      });
      if (!res.ok) {
        await throwApiErrorFromResponse(res, { method: "GET", url });
      }
      if (!res.body) throw new TypeError("SSE 响应无 body");
      established = await readStream(res); // 收到建连帧才算建连成功
    } catch {
      // 网络断开 / 服务端错误 / AbortError：统一走重连调度。
    } finally {
      if (cancelled) return;
      opts.onDisconnect?.();
      if (established) failures = 0; // 存活过的连接掉线不累计失败
      else failures += 1;
      if (failures >= maxFailures) {
        opts.onDegraded();
        scheduleReconnect(degradedRetryDelayMs);
      } else {
        scheduleReconnect(reconnectDelayMs);
      }
    }
  }

  connect();

  return {
    stop() {
      cancelled = true;
      if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
      activeController?.abort();
    },
  };
}
